import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi, type Agendamento, type EstadoAgendamento } from '../lib/api'
import { useSessao } from '../lib/sessao'
import { Carregando, Erro, Vazio } from '../componentes/ui'
import { hora } from '../lib/formato'
import './fila.css'

/**
 * A FILA — o dia operado por estado, não por hora.
 *
 * ─── POR QUE ELA EXISTE ───────────────────────────────────────────
 *
 * O Clinuma tinha grade e não tinha fila. A grade serve para PLANEJAR:
 * ela responde "cabe alguém às 15h?". Quem está trabalhando às 15h faz
 * outra pergunta — "quem chegou? quem está lá dentro? quem faltou?" —
 * e para isso a grade obriga a varrer a tela procurando cor.
 *
 * Todas as referências de painel clínico que o Lucas trouxe mostram
 * fila, não grade, na tela do dia. Entre "o paciente chegou" e "o
 * paciente foi embora pago e com evolução escrita", a recepção
 * atravessava quatro telas.
 *
 * ─── NÃO HÁ ROTA NOVA AQUI ────────────────────────────────────────
 *
 * É `GET /v1/agenda` do dia, reagrupado no cliente. Uma rota
 * `/v1/fila` seria conveniência: os mesmos dados, outro caminho, mais
 * uma superfície para manter em pé. Medir antes de criar.
 *
 * ─── O QUE NÃO É TEMPO REAL, E POR QUÊ ────────────────────────────
 *
 * Recarrega ao voltar o foco da janela e a cada 60s. Não existe
 * websocket no projeto, e fingir tempo real com intervalo curto gasta
 * bateria de tablet para prometer o que não se entrega.
 */

const COLUNAS: Array<{
  id: string
  titulo: string
  vazio: string
  estados: EstadoAgendamento[]
}> = [
  { id: 'esperados', titulo: 'Esperados', estados: ['AGENDADO', 'CONFIRMADO'],
    vazio: 'Ninguém para chegar.' },
  { id: 'recepcao', titulo: 'Na recepção', estados: ['CHEGOU'],
    vazio: 'Ninguém esperando no balcão.' },
  { id: 'atendendo', titulo: 'Em atendimento', estados: ['ATENDENDO'],
    vazio: 'Nenhuma sala ocupada.' },
  { id: 'concluidos', titulo: 'Concluídos', estados: ['ATENDIDO'],
    vazio: 'Nada concluído ainda.' },
  { id: 'faltaram', titulo: 'Não vieram', estados: ['FALTOU'],
    vazio: 'Ninguém faltou. Bom dia.' },
]

/** A ação que faz sentido em cada coluna — uma só, a mais provável. */
const AVANCO: Record<string, { estado: EstadoAgendamento; rotulo: string } | null> = {
  esperados:  { estado: 'CHEGOU',    rotulo: 'Chegou' },
  recepcao:   { estado: 'ATENDENDO', rotulo: 'Chamar' },
  atendendo:  { estado: 'ATENDIDO',  rotulo: 'Concluir' },
  concluidos: null,
  faltaram:   null,
}

const diaDeHoje = () => {
  const i = new Date(); i.setHours(0, 0, 0, 0)
  const f = new Date(i); f.setDate(f.getDate() + 1)
  return { de: i.toISOString(), ate: f.toISOString() }
}

export function Fila() {
  const { pode } = useSessao()
  const podeEscrever = pode('agenda:write')

  const [itens, setItens] = useState<Agendamento[]>([])
  const [espera, setEspera] = useState(0)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [mexendo, setMexendo] = useState<string | null>(null)

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true)
    try {
      const { de, ate } = diaDeHoje()
      const [g, e] = await Promise.all([
        api<{ agendamentos: Agendamento[] }>(`/v1/agenda?de=${de}&ate=${ate}`),
        api<{ total: number }>('/v1/espera').catch(() => ({ total: 0 })),
      ])
      setItens(g.agendamentos); setEspera(e.total); setErro(null)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar a fila.')
    } finally { setCarregando(false) }
  }, [])

  useEffect(() => { void carregar() }, [carregar])

  /* Volta o foco na janela → recarrega. É o gesto real da recepção:
     ela sai do sistema para atender o telefone e volta. */
  useEffect(() => {
    const aoFocar = () => void carregar(true)
    const relogio = setInterval(() => void carregar(true), 60_000)
    window.addEventListener('focus', aoFocar)
    return () => { window.removeEventListener('focus', aoFocar); clearInterval(relogio) }
  }, [carregar])

  async function avancar(a: Agendamento, estado: EstadoAgendamento) {
    setMexendo(a.id)
    try {
      await api(`/v1/agendamentos/${a.id}/estado`, { metodo: 'POST', corpo: { status: estado } })
      await carregar(true)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível mudar o estado.')
    } finally { setMexendo(null) }
  }

  if (carregando) return <Carregando altura={300} />

  const doDia = itens.filter((a) => !a.status.startsWith('CANCELADO') && a.status !== 'REMARCADO')

  return (
    <div className="fila">
      <Erro mensagem={erro} />

      <header className="fila__cab">
        <div>
          <h1>Fila de hoje</h1>
          <p className="ajuda">
            {doDia.length} no dia
            {espera > 0 && ` · ${espera} na lista de espera`}
            {' · atualiza sozinho a cada minuto'}
          </p>
        </div>
      </header>

      {doDia.length === 0 ? (
        <div className="cartao fila__nada">
          <Vazio
            mensagem="Nenhum atendimento hoje."
            sugestao={espera > 0
              ? `${espera} pessoa(s) esperam um horário. Talvez caiba alguém.`
              : 'Dia livre.'}
          />
        </div>
      ) : (
        <div className="fila__colunas">
          {COLUNAS.map((c) => {
            const dela = doDia.filter((a) => c.estados.includes(a.status))
            const avanco = AVANCO[c.id]
            return (
              <section key={c.id} className="fila__coluna">
                <header className="fila__coluna-cab">
                  <h2>{c.titulo}</h2>
                  <span className="fila__conta">{dela.length}</span>
                </header>

                {dela.length === 0 ? (
                  <p className="fila__vazio">{c.vazio}</p>
                ) : (
                  <ul className="fila__lista">
                    {dela.map((a) => {
                      const principal = a.alocacao.find((x) => x.principal)
                      return (
                        <li key={a.id} className="fila__cartao">
                          <div className="fila__linha1">
                            <span className="fila__hora mono">{hora(a.inicio)}</span>
                            <strong className="fila__nome">
                              {a.paciente_social || a.paciente || 'Sem paciente'}
                            </strong>
                          </div>

                          {/* Chips de ATRIBUTO: contorno, com ícone de
                              categoria. Não são selo — selo é estado, e
                              o estado aqui é a própria coluna. */}
                          <div className="fila__chips">
                            {a.procedimento && (
                              <span className="fila__chip">{a.procedimento}</span>
                            )}
                            {principal?.recurso && (
                              <span className="fila__chip">{principal.recurso}</span>
                            )}
                            {principal?.profissional && (
                              <span className="fila__chip fila__chip--quem">
                                {principal.profissional}
                              </span>
                            )}
                          </div>

                          {podeEscrever && avanco && (
                            <button className="botao botao--pequeno fila__avancar"
                                    disabled={mexendo === a.id}
                                    onClick={() => void avancar(a, avanco.estado)}>
                              {mexendo === a.id ? '…' : avanco.rotulo}
                            </button>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                )}
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}
